import React, { useState } from 'react';
import { Beaker } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

/**
 * SuperAdmin-only dev helper: flip the calling user's own `current_week`
 * so you can preview any week's UI state (e.g. does the Weekly Tasks tile
 * relabel to "Programme Week 3 Self-Work" once the 2WW closes?).
 *
 * Hits POST /api/admin/dev/set-my-current-week (superadmin-guarded).
 * Reloads the page after a successful flip so all week-derived UI re-hydrates.
 */
function DevPreviewWeekWidget() {
  const [week, setWeek] = useState('');
  const [saving, setSaving] = useState(false);

  const OPTIONS = [
    { label: '2WW · Week -2 (first day)', value: -2 },
    { label: '2WW · Week -1', value: -1 },
    { label: 'Week 0 (2WW final day)', value: 0 },
    { label: 'Programme Week 1', value: 1 },
    { label: 'Programme Week 3', value: 3 },
    { label: 'Programme Week 10', value: 10 },
    { label: 'Programme Week 20', value: 20 },
    { label: 'Programme Week 30 (final)', value: 30 },
  ];

  const applyWeek = async (target) => {
    setSaving(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dev/set-my-current-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week: target }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to set week');
        return;
      }
      toast.success(`Now previewing Week ${target}. Reloading…`);
      // Give the toast a beat to show, then hard reload so every week-derived
      // component re-fetches (dashboard, weekly-tasks tile, gates, banners).
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4"
      data-testid="dev-preview-week-widget"
    >
      <div className="flex items-center gap-2 mb-2">
        <Beaker className="w-4 h-4 text-amber-700" />
        <h3 className="text-sm font-bold text-amber-900">Dev · Preview Week</h3>
        <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-2 py-0.5 rounded">
          Superadmin only
        </span>
      </div>
      <p className="text-xs text-amber-800 mb-3">
        Flip your own <code className="font-mono bg-amber-100 px-1 rounded">current_week</code> to preview any point in the programme (e.g. verify the Weekly Tasks tile auto-swap after the 2 Week Window closes). Only affects your account: no other user is impacted.
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        {OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => applyWeek(opt.value)}
            disabled={saving}
            className="text-xs px-2 py-1 rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
            data-testid={`preview-week-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={-2}
          max={32}
          value={week}
          onChange={(e) => setWeek(e.target.value)}
          placeholder="Custom week (-2 to 32)"
          className="flex-1 text-xs px-2 py-1 rounded border border-amber-300 bg-white text-amber-900"
          data-testid="preview-week-custom-input"
        />
        <button
          onClick={() => {
            const n = parseInt(week, 10);
            if (Number.isNaN(n) || n < -2 || n > 32) {
              toast.error('Week must be between -2 and 32');
              return;
            }
            applyWeek(n);
          }}
          disabled={saving || week === ''}
          className="text-xs px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          data-testid="preview-week-custom-apply"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

export default DevPreviewWeekWidget;
